import { deviceType, pageIsDraft, findOrCreateDeviceId, } from "@theconversation/shared-components/tcEventsCollector";
import { currentRegionFromMetaTag } from "@theconversation/shared-components/currentRegionFromMetaTag";
import { dispatchEvent } from "./dispatchEvent";
import { pageConfigContentId } from "./globalConfig";
import { isSessionActive } from "../currentUser/session";
import { getCurrentActiveTimeOnPage } from "../activeTimeOnPage";
import { maxArticleScrollDepth } from "../articleScrollDepth";
// This module implements a very simple event dispatch system.
// It will try to use a persistent deviceId for a given user's browser.
// The general schema of the events is common data at the top level, and event-specific
// data nested under a key named after the event.
// In the current iteration, article view and promo events are blocked if they are
// coming from a draft editor preview. Potentially a future version should instead
// set an attribute (like "location: draft") instead, but while this is an experiment
// it's better to keep things simple.
export function trackArticleViewed(contentId) {
    if (pageIsDraft())
        return;
    const event = {
        eventType: "articleViewed",
        deviceId: findOrCreateDeviceId(),
        userLoggedIn: isSessionActive(),
        activeEdition: currentRegionFromMetaTag(),
        articleViewed: {
            contentId: contentId.toString(),
            deviceType: deviceType(),
        },
    };
    dispatchEvent(event);
}
/**
 * Emit a tracking event to say the page has lost focus (which is the last event the page can emit before being closed).
 *
 * Note that because this event is sent from a visibility change handler, it is a good idea to avoid async code, in
 * case that causes the event to not get sent at all before the JS engine is terminated for the page.
 *
 * Note that this event is sent when the user changes tabs or puts their device to sleep, AND when the page is
 * closed finally. So in the data warehouse there will need to be a view that just takes the final event.
 */
export function trackArticlePageLostFocus(contentId) {
    const event = {
        eventType: "articlePageLostFocus",
        deviceId: findOrCreateDeviceId(),
        userLoggedIn: isSessionActive(),
        activeEdition: currentRegionFromMetaTag(),
        articlePageLostFocus: {
            contentId: contentId.toString(),
            deviceType: deviceType(),
            activeTimeOnPageSeconds: getCurrentActiveTimeOnPage(),
            maxScrollDepth: maxArticleScrollDepth(),
        },
    };
    dispatchEvent(event, true); // Dispatch immediately as page might be about to end
}
// The caller of the promo tracking functions may not actually have the contentId,
// so we fetch that independently if it's missing.
export function trackPromoViewed(promoId, promoType, contentId = findContentId()) {
    if (pageIsDraft())
        return;
    const event = {
        eventType: "promoViewed",
        deviceId: findOrCreateDeviceId(),
        userLoggedIn: isSessionActive(),
        activeEdition: currentRegionFromMetaTag(),
        promoViewed: {
            contentId: contentId.toString(),
            promoId: promoId.toString(),
            promoType: promoType,
            deviceType: deviceType(),
        },
    };
    dispatchEvent(event);
}
export function trackNewsletterSubscribed(newsletterId, promoId, promoType) {
    if (pageIsDraft())
        return;
    const event = {
        eventType: "newsletterSubscribed",
        deviceId: findOrCreateDeviceId(),
        userLoggedIn: isSessionActive(),
        activeEdition: currentRegionFromMetaTag(),
        newsletterSubscribed: {
            deviceType: deviceType(),
            newsletterId: newsletterId,
            contentId: findContentId().toString(),
            promoId: promoId,
            promoType: promoType,
        },
    };
    dispatchEvent(event);
}
// The caller of the promo tracking functions may not actually have the contentId,
// so we fetch that independently if it's missing.
export function trackPromoEngaged(promoId, promoType, contentId = findContentId()) {
    if (pageIsDraft())
        return;
    const event = {
        eventType: "promoEngaged",
        deviceId: findOrCreateDeviceId(),
        userLoggedIn: isSessionActive(),
        activeEdition: currentRegionFromMetaTag(),
        promoEngaged: {
            contentId: contentId.toString(),
            promoId: promoId.toString(),
            promoType: promoType,
            deviceType: deviceType(),
        },
    };
    dispatchEvent(event, true);
}
// Try to locate the current page's content id by inspecting the DOM.
// There are a couple of ways to try and find the content id, so try both.
function findContentId() {
    const globalPageContentId = pageConfigContentId();
    if (globalPageContentId) {
        return globalPageContentId;
    }
    const element = document.querySelector("link[rel='canonical']");
    if (element && element instanceof HTMLLinkElement) {
        return element.href.match(/-(\d{3,9})$/)?.[1] || "";
    }
    else {
        return "";
    }
}
